import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { History, AlertCircle, CheckCircle2, RotateCcw } from 'lucide-react'
import { isAxiosError } from 'axios'
import { apiClient } from '@/lib/api.ts'
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'

interface HistoryEntry {
  id: string
  version: number
  is_active: number
  notes: string | null
  created_at: string
  created_by_user_id: string | null
}

type RestoreOpKind =
  | 'restore_folder'
  | 'delete_folder'
  | 'rename_folder'
  | 'restore_group'
  | 'delete_group'
  | 'rename_group'
  | 'restore_ou'
  | 'delete_ou'
  | 'rename_ou'
  | 'restore_server'
  | 'delete_server'
  | 'rename_server'
  | 'restore_share'
  | 'delete_share'
  | 'update_share'
  | 'restore_drive'
  | 'delete_drive'
  | 'update_drive'
  | 'add_permission'
  | 'remove_permission'
  | 'update_permission'

interface RestoreOp {
  opId: string
  kind: RestoreOpKind
  id: string
  label: string
}

interface DiffResponse {
  operations: RestoreOp[]
  currentVersionId: string
  targetVersion: number
  isCurrent: boolean
}

const GROUP_LABELS: Record<string, string> = {
  folders: 'Ordner',
  groups: 'Gruppen',
  ous: 'OUs',
  servers: 'Server & Shares',
  drives: 'Laufwerke',
  permissions: 'Berechtigungen',
}

function opGroup(kind: RestoreOpKind): keyof typeof GROUP_LABELS {
  if (kind.endsWith('folder')) return 'folders'
  if (kind.endsWith('group')) return 'groups'
  if (kind.endsWith('ou')) return 'ous'
  if (kind.endsWith('server') || kind.endsWith('share')) return 'servers'
  if (kind.endsWith('drive')) return 'drives'
  return 'permissions'
}

function opText(op: RestoreOp): string {
  switch (op.kind) {
    case 'restore_folder': return `Ordner „${op.label}" wiederherstellen`
    case 'delete_folder': return `Ordner „${op.label}" entfernen`
    case 'rename_folder': return `Ordner umbenennen: ${op.label}`
    case 'restore_group': return `Gruppe „${op.label}" wiederherstellen`
    case 'delete_group': return `Gruppe „${op.label}" entfernen`
    case 'rename_group': return `Gruppe umbenennen: ${op.label}`
    case 'restore_ou': return `OU „${op.label}" wiederherstellen`
    case 'delete_ou': return `OU „${op.label}" entfernen`
    case 'rename_ou': return `OU umbenennen: ${op.label}`
    case 'restore_server': return `Server „${op.label}" wiederherstellen`
    case 'delete_server': return `Server „${op.label}" entfernen`
    case 'rename_server': return `Server umbenennen: ${op.label}`
    case 'restore_share': return `Share „${op.label}" wiederherstellen`
    case 'delete_share': return `Share „${op.label}" entfernen`
    case 'update_share': return `Share-Pfad ändern: ${op.label}`
    case 'restore_drive': return `Laufwerk ${op.label} wiederherstellen`
    case 'delete_drive': return `Laufwerk ${op.label} entfernen`
    case 'update_drive': return `Laufwerk ändern: ${op.label}`
    case 'add_permission': return `Berechtigung hinzufügen: ${op.label}`
    case 'remove_permission': return `Berechtigung entfernen: ${op.label}`
    case 'update_permission': return `Berechtigung ändern: ${op.label}`
  }
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

interface Props {
  open: boolean
  customerId: string
  onClose: () => void
}

export function VersionHistoryModal({ open, customerId, onClose }: Props) {
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const history = useQuery<{ history: HistoryEntry[] }>({
    queryKey: ['it-history', customerId],
    queryFn: () =>
      apiClient.get(`/it-structures/customers/${customerId}/history`).then((r) => r.data),
    enabled: open && !!customerId,
  })

  const activeId = useMemo(() => {
    const list = history.data?.history ?? []
    return list.find((h) => h.is_active)?.id ?? list[0]?.id ?? null
  }, [history.data])

  useEffect(() => {
    if (!open) return
    if (selectedId) return
    const list = history.data?.history ?? []
    const firstOld = list.find((h) => h.id !== activeId)
    if (firstOld) setSelectedId(firstOld.id)
  }, [open, history.data, activeId, selectedId])

  const diff = useQuery<DiffResponse>({
    queryKey: ['it-history-diff', customerId, selectedId],
    queryFn: () =>
      apiClient
        .get(`/it-structures/customers/${customerId}/versions/${selectedId}/diff`)
        .then((r) => r.data),
    enabled: open && !!customerId && !!selectedId,
  })

  useEffect(() => {
    setChecked(new Set())
    setRestoreError(null)
  }, [selectedId])

  useEffect(() => {
    if (!open) {
      setSelectedId(null)
      setChecked(new Set())
      setRestoreError(null)
      setSuccessMsg(null)
    }
  }, [open])

  const restore = useMutation({
    mutationFn: async () =>
      apiClient.post(
        `/it-structures/customers/${customerId}/versions/${selectedId}/restore`,
        { opIds: Array.from(checked) },
      ),
    onSuccess: () => {
      setSuccessMsg(
        `${checked.size} Änderung${checked.size === 1 ? '' : 'en'} wiederhergestellt — neue Version angelegt.`,
      )
      setRestoreError(null)
      qc.invalidateQueries({ queryKey: ['it-customer-structure', customerId] })
      qc.invalidateQueries({ queryKey: ['it-history', customerId] })
      qc.invalidateQueries({ queryKey: ['it-history-diff', customerId] })
      setTimeout(() => {
        setSuccessMsg(null)
        onClose()
      }, 1500)
    },
    onError: (err: unknown) => {
      if (isAxiosError(err)) {
        setRestoreError(err.response?.data?.error ?? err.message)
      } else {
        setRestoreError(err instanceof Error ? err.message : 'Unbekannter Fehler')
      }
    },
  })

  const groupedOps = useMemo(() => {
    const ops = diff.data?.operations ?? []
    const groups: Record<string, RestoreOp[]> = {}
    for (const op of ops) {
      const g = opGroup(op.kind)
      if (!groups[g]) groups[g] = []
      groups[g]!.push(op)
    }
    return groups
  }, [diff.data])

  const totalOps = diff.data?.operations.length ?? 0

  function toggleOp(opId: string) {
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(opId)) next.delete(opId)
      else next.add(opId)
      return next
    })
  }

  function toggleGroup(ops: RestoreOp[]) {
    const allChecked = ops.every((o) => checked.has(o.opId))
    setChecked((prev) => {
      const next = new Set(prev)
      for (const o of ops) {
        if (allChecked) next.delete(o.opId)
        else next.add(o.opId)
      }
      return next
    })
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      size="xl"
      className="h-[85vh]"
      title={
        <span className="flex items-center gap-2">
          <History className="w-5 h-5 text-brand-500" />
          Versions-Historie
        </span>
      }
      footer={
        <div className="flex items-center justify-between gap-3 w-full">
          <div className="text-caption text-gray-600 min-w-0 flex-1">
            {successMsg && (
              <span className="text-green-700 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> {successMsg}
              </span>
            )}
            {restoreError && (
              <span className="text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {restoreError}
              </span>
            )}
            {!successMsg && !restoreError && checked.size > 0 && (
              <span>
                {checked.size} Änderung{checked.size === 1 ? '' : 'en'} ausgewählt
              </span>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Schließen
          </Button>
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<RotateCcw className="w-4 h-4" />}
            loading={restore.isPending}
            disabled={checked.size === 0 || restore.isPending}
            onClick={() => restore.mutate()}
          >
            Auswahl wiederherstellen
          </Button>
        </div>
      }
    >
      <div className="flex min-h-0 h-full -mx-5 -my-5">
        <div className="w-72 border-r border-gray-200 overflow-y-auto">
          {history.isLoading && <LoadingState size="sm" label="Lade Historie…" />}
          {history.error && (
            <ErrorState
              size="sm"
              title="Fehler beim Laden der Historie"
              description={history.error instanceof Error ? history.error.message : undefined}
            />
          )}
          {history.data && history.data.history.length === 0 && (
            <EmptyState size="sm" title="Noch keine Versionen vorhanden" />
          )}
          <ul className="divide-y divide-gray-100">
            {(history.data?.history ?? []).map((h) => {
              const isActive = h.id === activeId
              const isSelected = h.id === selectedId
              return (
                <li key={h.id}>
                  <button
                    onClick={() => setSelectedId(h.id)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors',
                      isSelected && 'bg-brand-50 hover:bg-brand-50',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-caption text-gray-700">v{h.version}</span>
                      {isActive && (
                        <Badge variant="success" size="sm">Aktiv</Badge>
                      )}
                    </div>
                    <div className="text-caption text-gray-500 mt-0.5">{formatTime(h.created_at)}</div>
                    {h.notes && (
                      <div className="text-caption text-gray-600 mt-1 line-clamp-2">{h.notes}</div>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!selectedId && (
            <div className="p-6 text-body text-gray-500">
              Wähle links eine Version aus, um den Vergleich mit der aktuellen Struktur anzuzeigen.
            </div>
          )}
          {selectedId && diff.isLoading && <LoadingState size="sm" label="Berechne Diff…" />}
          {selectedId && diff.error && (
            <ErrorState size="sm" title="Fehler beim Berechnen des Diffs" />
          )}
          {selectedId && diff.data && diff.data.isCurrent && (
            <div className="p-6 text-body text-gray-500">
              Diese Version ist die aktuell aktive Struktur.
            </div>
          )}
          {selectedId && diff.data && !diff.data.isCurrent && totalOps === 0 && (
            <EmptyState
              size="sm"
              title="Keine Unterschiede"
              description="Zwischen dieser Version und dem aktuellen Stand gibt es keine Unterschiede."
            />
          )}
          {selectedId && diff.data && !diff.data.isCurrent && totalOps > 0 && (
            <div className="p-4 space-y-4">
              <div className="text-caption text-gray-600">
                {totalOps} Änderung{totalOps === 1 ? '' : 'en'} zwischen Version{' '}
                {diff.data.targetVersion} und aktueller Struktur. Wähle die Änderungen aus, die
                wiederhergestellt werden sollen.
              </div>

              {Object.entries(GROUP_LABELS).map(([g, label]) => {
                const ops = groupedOps[g]
                if (!ops || ops.length === 0) return null
                const allChecked = ops.every((o) => checked.has(o.opId))
                const someChecked = ops.some((o) => checked.has(o.opId))
                return (
                  <div key={g} className="border border-gray-200 rounded-md">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                      <div className="text-body font-medium text-gray-900">{label}</div>
                      <Checkbox
                        checked={allChecked}
                        indeterminate={!allChecked && someChecked}
                        onChange={() => toggleGroup(ops)}
                        label={`Alle (${ops.length})`}
                      />
                    </div>
                    <ul className="divide-y divide-gray-100">
                      {ops.map((op) => (
                        <li key={op.opId} className="px-3 py-2 hover:bg-gray-50">
                          <Checkbox
                            checked={checked.has(op.opId)}
                            onChange={() => toggleOp(op.opId)}
                            label={<span className="text-body text-gray-700">{opText(op)}</span>}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
